import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types/api';

/**
 * Extract the tenant slug from the current hostname.
 * Expected format: {slug}.churchcrm.app or {slug}.localhost
 * Falls back to localStorage for Capacitor native apps.
 */
function getTenantSlug(): string | null {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // If we have a subdomain (e.g., mychurch.churchcrm.app)
  if (parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'app') {
    return parts[0] ?? null;
  }

  // Fallback: localStorage (Capacitor / mobile / local dev)
  const stored = localStorage.getItem('tenant_slug');
  if (stored) return stored;

  // Default for local development (single-tenant)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_DEFAULT_TENANT || 'betel-arad';
  }

  return null;
}

function getStoredToken(): string | null {
  return localStorage.getItem('access_token');
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30_000,
});

// Request interceptor: attach auth token and tenant slug
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// Token refresh state — shared across concurrent 401s
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

// Response interceptor: handle errors globally with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response) {
      const { status } = error.response;

      // Attempt token refresh on 401 (except for auth endpoints)
      if (
        status === 401 &&
        !originalRequest._retry &&
        originalRequest.url &&
        !originalRequest.url.includes('/auth/login') &&
        !originalRequest.url.includes('/auth/refresh')
      ) {
        if (isRefreshing) {
          // Queue this request until the refresh completes
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken: string) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(
            `${apiClient.defaults.baseURL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${getStoredToken()}`,
                'X-Tenant-Slug': getTenantSlug() ?? '',
                Accept: 'application/json',
              },
            },
          );

          const newToken = response.data.token as string;
          localStorage.setItem('access_token', newToken);

          // Notify queued requests
          onTokenRefreshed(newToken);
          isRefreshing = false;

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch {
          isRefreshing = false;
          refreshSubscribers = [];

          // Refresh failed — force re-login
          localStorage.removeItem('access_token');
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      }

      if (status === 401) {
        // Auth endpoints or already retried — force re-login
        localStorage.removeItem('access_token');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }

      if (status === 403) {
        console.error('Access denied: insufficient permissions');
      }

      if (status === 404) {
        console.error('Resource not found');
      }

      if (status >= 500) {
        console.error('Server error — please try again later');
      }
    } else if (error.request) {
      console.error('Network error — please check your connection');
    }

    return Promise.reject(error);
  },
);

export default apiClient;
export { getTenantSlug, getStoredToken };
